import React from 'react';
import { usePlaidLink } from 'react-plaid-link';
import DashboardMenu from '../../components/dashboardMenu/dashboardMenu';
import Grid from '../../components/grid/grid';
import useApi, { CONTROLLER } from '../../hooks/useApi';
import '../strategies/strategies.css';
import './brokerPortfolio.css';

const showBrokerPortfolio =
  process.env.REACT_APP_SHOW_BROKER_PORTFOLIO === '1' || process.env.NODE_ENV !== 'production';
const PLAID_OAUTH_STATE_PARAM = 'oauth_state_id';
const PLAID_LINK_TOKEN_STORAGE_KEY = 'dt.plaid.linkToken';
const PLAID_LINK_ITEM_STORAGE_KEY = 'dt.plaid.itemId';
const PLAID_LINK_TOKEN_CREATED_AT_STORAGE_KEY = 'dt.plaid.linkTokenCreatedAt';
const PLAID_LINK_TOKEN_MAX_AGE_MS = 30 * 60 * 1000;
const BROKER_TABLE_MIN_HEIGHT = 560;
const BROKER_TABLE_MAX_HEIGHT = 1400;
const BROKER_TABLE_DEFAULT_HEIGHT = 760;
const BROKER_PANEL_MIN_WIDTH_PERCENT = 88;
const BROKER_PANEL_DEFAULT_WIDTH_PERCENT = 100;
const BROKER_GRID_PREFERENCE = 'brokerPortfolioHoldings_v3';
const BROKER_HIDDEN_ENTRIES_STORAGE_KEY = 'dt.broker.hiddenEntries.v1';
const BROKER_RESIZE_MODE_Y = 'y';
const BROKER_RESIZE_MODE_X = 'x';
const BROKER_RESIZE_MODE_BOTH = 'both';
const BROKER_RESIZE_CLASS_Y = 'dt-broker-resizing-y';
const BROKER_RESIZE_CLASS_X = 'dt-broker-resizing-x';
const BROKER_RESIZE_CLASS_BOTH = 'dt-broker-resizing-both';

const readPlaidStoredValue = (key) => {
  if (typeof window === 'undefined') return '';
  try {
    return String(window.localStorage.getItem(key) || '');
  } catch (e) {
    return '';
  }
};

const writePlaidStoredValue = (key, value) => {
  if (typeof window === 'undefined') return;
  try {
    if (value === null || value === undefined || value === '') {
      window.localStorage.removeItem(key);
      return;
    }
    window.localStorage.setItem(key, String(value));
  } catch (e) {
    // ignore storage errors
  }
};

const clearPlaidStoredState = () => {
  writePlaidStoredValue(PLAID_LINK_TOKEN_STORAGE_KEY, '');
  writePlaidStoredValue(PLAID_LINK_ITEM_STORAGE_KEY, '');
  writePlaidStoredValue(PLAID_LINK_TOKEN_CREATED_AT_STORAGE_KEY, '');
};

const hasPlaidOauthState = () => {
  if (typeof window === 'undefined') return false;
  try {
    const params = new URLSearchParams(window.location.search || '');
    return params.has(PLAID_OAUTH_STATE_PARAM);
  } catch (e) {
    return false;
  }
};

const getPlaidReceivedRedirectUri = () => {
  if (typeof window === 'undefined') return null;
  if (!hasPlaidOauthState()) return null;
  return window.location.href;
};

const clearPlaidOauthQueryString = () => {
  if (typeof window === 'undefined') return;
  if (!hasPlaidOauthState()) return;
  try {
    const { pathname, hash } = window.location;
    const nextUrl = `${pathname}${hash || ''}`;
    window.history.replaceState({}, document.title, nextUrl);
  } catch (e) {
    // ignore history errors
  }
};

const getPlaidRedirectUriForRequest = () => {
  const configured = String(process.env.REACT_APP_PLAID_REDIRECT_URI || '').trim();
  if (!configured) {
    return null;
  }
  return configured;
};

const getStoredPlaidLinkTokenIfFresh = () => {
  const token = readPlaidStoredValue(PLAID_LINK_TOKEN_STORAGE_KEY);
  if (!token) {
    return '';
  }

  const createdAtRaw = readPlaidStoredValue(PLAID_LINK_TOKEN_CREATED_AT_STORAGE_KEY);
  const createdAtMs = Number(createdAtRaw);
  const nowMs = Date.now();

  if (!Number.isFinite(createdAtMs) || createdAtMs <= 0 || (nowMs - createdAtMs) > PLAID_LINK_TOKEN_MAX_AGE_MS) {
    clearPlaidStoredState();
    return '';
  }

  return token;
};

const clampBrokerTableHeight = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) {
    return BROKER_TABLE_DEFAULT_HEIGHT;
  }
  return Math.max(BROKER_TABLE_MIN_HEIGHT, Math.min(BROKER_TABLE_MAX_HEIGHT, Math.round(num)));
};

const clampBrokerPanelWidthPercent = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) {
    return BROKER_PANEL_DEFAULT_WIDTH_PERCENT;
  }
  return Math.max(BROKER_PANEL_MIN_WIDTH_PERCENT, num);
};

const clearBrokerResizeBodyClasses = () => {
  if (typeof document === 'undefined') return;
  document.body.classList.remove(BROKER_RESIZE_CLASS_Y, BROKER_RESIZE_CLASS_X, BROKER_RESIZE_CLASS_BOTH);
};

const setBrokerResizeBodyClass = (mode) => {
  if (typeof document === 'undefined') return;
  clearBrokerResizeBodyClasses();
  if (mode === BROKER_RESIZE_MODE_X) {
    document.body.classList.add(BROKER_RESIZE_CLASS_X);
    return;
  }
  if (mode === BROKER_RESIZE_MODE_BOTH) {
    document.body.classList.add(BROKER_RESIZE_CLASS_BOTH);
    return;
  }
  document.body.classList.add(BROKER_RESIZE_CLASS_Y);
};

const formatMoney = (value, currency) => {
  const num = Number(value);
  if (!Number.isFinite(num)) {
    return '—';
  }
  const cur = String(currency || 'USD').toUpperCase();
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: cur,
      maximumFractionDigits: 2,
    }).format(num);
  } catch (e) {
    return num.toFixed(2);
  }
};

const formatSignedMoneyDelta = (value, currency) => {
  const num = Number(value);
  if (!Number.isFinite(num)) {
    return '—';
  }
  const base = formatMoney(Math.abs(num), currency);
  if (base === '—') {
    return '—';
  }
  if (num > 0) {
    return `+${base}`;
  }
  if (num < 0) {
    return `-${base}`;
  }
  return base;
};

const gainLossTextColor = (ratio) => {
  if (!Number.isFinite(ratio)) {
    return undefined;
  }
  const maxAbsPct = 10;
  const absPct = Math.min(Math.abs(ratio * 100), maxAbsPct);
  const t = absPct / maxAbsPct;
  const lightness = 85 - (t * 25);
  const hue = ratio >= 0 ? 120 : 0;
  return `hsl(${hue}, 70%, ${lightness}%)`;
};

const formatGainLossPct = (ratio) => {
  if (!Number.isFinite(ratio)) {
    return '—';
  }
  const pct = ratio * 100;
  const sign = pct > 0 ? '+' : '';
  return `${sign}${pct.toFixed(2)}%`;
};

const toFiniteNumberOrNull = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) {
    return null;
  }
  return num;
};

const getApiMessage = (resp, fallback) => {
  if (resp && typeof resp.message === 'string' && resp.message.trim()) {
    return resp.message;
  }
  if (resp && typeof resp.error === 'string' && resp.error.trim()) {
    return resp.error;
  }
  return fallback;
};

const extractLinkTokenFromApiResponse = (resp) => {
  const candidates = [
    resp?.data?.linkToken,
    resp?.data?.link_token,
    resp?.data?.token,
    resp?.data?.data?.linkToken,
    resp?.data?.data?.link_token,
    resp?.data?.data?.token,
    resp?.linkToken,
    resp?.link_token,
    resp?.token,
  ];
  for (const candidate of candidates) {
    const token = String(candidate || '').trim();
    if (token) {
      return token;
    }
  }
  return '';
};

const normalizeHiddenEntryKeys = (value) => {
  if (!Array.isArray(value)) {
    return [];
  }
  const unique = new Set();
  value.forEach((item) => {
    const key = String(item || '').trim();
    if (key) {
      unique.add(key);
    }
  });
  return Array.from(unique);
};

const readBrokerHiddenEntryKeys = () => {
  if (typeof window === 'undefined') {
    return [];
  }
  try {
    const raw = window.localStorage.getItem(BROKER_HIDDEN_ENTRIES_STORAGE_KEY);
    if (!raw) {
      return [];
    }
    return normalizeHiddenEntryKeys(JSON.parse(raw));
  } catch (e) {
    return [];
  }
};

const writeBrokerHiddenEntryKeys = (keys) => {
  if (typeof window === 'undefined') {
    return;
  }
  const normalized = normalizeHiddenEntryKeys(keys);
  try {
    if (!normalized.length) {
      window.localStorage.removeItem(BROKER_HIDDEN_ENTRIES_STORAGE_KEY);
      return;
    }
    window.localStorage.setItem(BROKER_HIDDEN_ENTRIES_STORAGE_KEY, JSON.stringify(normalized));
  } catch (e) {
    // ignore storage errors
  }
};

const getBrokerHiddenEntryKey = ({ accountId, securityId, ticker, name }) => {
  const acct = String(accountId || '').trim();
  const sec = String(securityId || '').trim();
  const sym = String(ticker || '').trim().toUpperCase();
  const label = String(name || '').trim().toLowerCase();

  if (acct && sec) {
    return `acct:${acct}|sec:${sec}`;
  }
  if (acct && sym) {
    return `acct:${acct}|tkr:${sym}`;
  }
  if (sym && label) {
    return `tkr:${sym}|name:${label}`;
  }
  if (sym) {
    return `tkr:${sym}`;
  }
  return '';
};

const BrokerPortfolio = () => {
  const { execute: executeLinkToken, payload: payloadLinkToken, loading: loadingLinkToken } = useApi(CONTROLLER.BROKER_LINK_TOKEN);
  const { execute: executeExchange, loading: loadingExchange } = useApi(CONTROLLER.BROKER_EXCHANGE);
  const { execute: executeAccountUpdate, loading: loadingAccountUpdate } = useApi(CONTROLLER.BROKER_ACCOUNTS_UPDATE);
  const { execute: executeDisconnect, loading: loadingDisconnect } = useApi(CONTROLLER.BROKER_DISCONNECT);
  const { execute: executeRefreshRequest, loading: loadingRefreshRequest } = useApi(CONTROLLER.BROKER_REFRESH);
  const { execute: executeHoldings, payload: payloadHoldings, loading: loadingHoldings } = useApi(CONTROLLER.BROKER_HOLDINGS);
  const { execute: executeRequestSupport, loading: loadingSupport } = useApi(CONTROLLER.BROKER_REQUEST_SUPPORT);

  const [linkToken, setLinkToken] = React.useState(() => getStoredPlaidLinkTokenIfFresh());
  const [receivedRedirectUri] = React.useState(() => getPlaidReceivedRedirectUri());
  const pendingLinkOpenRef = React.useRef(false);
  const pendingLinkTokenForItemRef = React.useRef(null);
  const refreshRequestInFlightRef = React.useRef(false);
  const oauthAutoOpenDoneRef = React.useRef(false);
  const holdingsCardRef = React.useRef(null);
  const tableResizeStateRef = React.useRef({
    active: false,
    mode: BROKER_RESIZE_MODE_Y,
    startX: 0,
    startY: 0,
    startContainerWidth: 0,
    startHeight: BROKER_TABLE_DEFAULT_HEIGHT,
    startWidthPercent: BROKER_PANEL_DEFAULT_WIDTH_PERCENT,
  });
  const [connections, setConnections] = React.useState([]);
  const [holdings, setHoldings] = React.useState([]);
  const [unsupported, setUnsupported] = React.useState([]);
  const [message, setMessage] = React.useState('');
  const [panelHeight, setPanelHeight] = React.useState(BROKER_TABLE_DEFAULT_HEIGHT);
  const [panelWidthPercent, setPanelWidthPercent] = React.useState(BROKER_PANEL_DEFAULT_WIDTH_PERCENT);
  const [hiddenEntryKeys, setHiddenEntryKeys] = React.useState(() => readBrokerHiddenEntryKeys());

  React.useEffect(() => {
    if (!showBrokerPortfolio) return;
    // Holdings endpoint includes connection metadata; keep a single network call on mount.
    executeHoldings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    const token = extractLinkTokenFromApiResponse(payloadLinkToken);
    if (token) {
      setLinkToken(token);
      writePlaidStoredValue(PLAID_LINK_TOKEN_STORAGE_KEY, token);
      writePlaidStoredValue(PLAID_LINK_TOKEN_CREATED_AT_STORAGE_KEY, Date.now());
    }
  }, [payloadLinkToken]);

  React.useEffect(() => {
    const data = payloadHoldings?.data || {};
    setConnections((prev) => (Array.isArray(data.connections) ? data.connections : prev));
    setHoldings(Array.isArray(data.holdings) ? data.holdings : []);
    setUnsupported(Array.isArray(data.unsupported) ? data.unsupported : []);
    if (payloadHoldings && payloadHoldings.success === false) {
      setMessage(payloadHoldings.message || 'Unable to load broker holdings.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payloadHoldings]);

  React.useEffect(() => {
    writeBrokerHiddenEntryKeys(hiddenEntryKeys);
  }, [hiddenEntryKeys]);

  React.useEffect(() => {
    if (!receivedRedirectUri) return;
    if (linkToken) return;
    clearPlaidOauthQueryString();
    clearPlaidStoredState();
  }, [receivedRedirectUri, linkToken]);

  const onSuccess = React.useCallback(async (publicToken, metadata) => {
    clearPlaidStoredState();
    clearPlaidOauthQueryString();
    setLinkToken('');
    setMessage('Finalizing broker connection…');
    const exchangeResp = await executeExchange({
      publicToken,
      institution: metadata?.institution || null,
    });
    if (!exchangeResp || exchangeResp.success === false) {
      setMessage(getApiMessage(exchangeResp, 'Broker connection failed. Please try again.'));
      return;
    }
    await executeHoldings();
    await executeLinkToken();
    setMessage('Broker connected.');
  }, [executeExchange, executeHoldings, executeLinkToken]);

  const onExit = React.useCallback((error, metadata) => {
    pendingLinkOpenRef.current = false;
    pendingLinkTokenForItemRef.current = null;
    clearPlaidStoredState();
    clearPlaidOauthQueryString();
    setLinkToken('');

    if (!error) {
      setMessage('Broker connection canceled.');
      return;
    }
    const plaidMessage = error.display_message || error.error_message || error.message;
    const errorCode = error.error_code ? String(error.error_code) : '';
    const errorType = error.error_type ? String(error.error_type) : '';
    const institution = metadata?.institution?.name ? String(metadata.institution.name) : '';
    const status = metadata?.status ? String(metadata.status) : '';

    const details = [errorCode, errorType, status].filter(Boolean).join(' / ');
    const prefix = institution ? `${institution}: ` : '';
    if (plaidMessage) {
      const detailText = details ? ` (${details})` : '';
      setMessage(`Broker connection failed: ${prefix}${plaidMessage}${detailText}`);
      return;
    }
    const fallbackDetailText = details ? ` (${details})` : '';
    setMessage(`Broker connection failed.${fallbackDetailText}`);
  }, []);

  const { open, ready } = usePlaidLink({
    token: linkToken || null,
    onSuccess,
    onExit,
    receivedRedirectUri,
  });

  React.useEffect(() => {
    if (pendingLinkOpenRef.current && ready && linkToken) {
      pendingLinkOpenRef.current = false;
      open();
    }
  }, [ready, linkToken, open]);

  React.useEffect(() => {
    if (!receivedRedirectUri) return;
    if (oauthAutoOpenDoneRef.current) return;
    if (!ready || !linkToken) return;
    oauthAutoOpenDoneRef.current = true;
    open();
  }, [ready, linkToken, open, receivedRedirectUri]);

  const openPlaidIfReady = React.useCallback(() => {
    if (!ready || !linkToken) {
      return false;
    }
    pendingLinkOpenRef.current = false;
    open();
    return true;
  }, [ready, linkToken, open]);

  const handleConnect = React.useCallback(async () => {
    if (loadingLinkToken || loadingExchange) {
      setMessage('Preparing secure broker link…');
      return;
    }
    if (openPlaidIfReady()) {
      setMessage('');
      return;
    }
    setMessage('Preparing secure broker link…');
    // Force re-init in case backend returns an equal link token value.
    setLinkToken('');
    pendingLinkTokenForItemRef.current = null;
    writePlaidStoredValue(PLAID_LINK_ITEM_STORAGE_KEY, '');
    pendingLinkOpenRef.current = true;
    const resp = await executeLinkToken({ redirectUri: getPlaidRedirectUriForRequest() });
    if (!resp || resp.success === false) {
      pendingLinkOpenRef.current = false;
      clearPlaidStoredState();
      setMessage(getApiMessage(resp, 'Unable to start broker connection. Please try again.'));
      return;
    }
    const token = extractLinkTokenFromApiResponse(resp);
    if (!token) {
      pendingLinkOpenRef.current = false;
      clearPlaidStoredState();
      setMessage(getApiMessage(resp, 'Unable to start broker connection. Please try again.'));
      return;
    }
    setLinkToken(token);
    writePlaidStoredValue(PLAID_LINK_TOKEN_STORAGE_KEY, token);
  }, [executeLinkToken, loadingLinkToken, loadingExchange, openPlaidIfReady]);

  const handleReconnect = React.useCallback(async (plaidItemRowId) => {
    if (loadingLinkToken || loadingExchange) {
      setMessage('Preparing secure reconnect…');
      return;
    }
    setMessage('Preparing secure reconnect…');
    // Force re-init in case backend returns an equal link token value.
    setLinkToken('');
    pendingLinkTokenForItemRef.current = plaidItemRowId;
    writePlaidStoredValue(PLAID_LINK_ITEM_STORAGE_KEY, plaidItemRowId);
    pendingLinkOpenRef.current = true;
    const resp = await executeLinkToken({
      plaidItemRowId,
      redirectUri: getPlaidRedirectUriForRequest(),
    });
    if (!resp || resp.success === false) {
      pendingLinkOpenRef.current = false;
      clearPlaidStoredState();
      setMessage(getApiMessage(resp, 'Unable to reconnect. Please try again.'));
      return;
    }
    const token = extractLinkTokenFromApiResponse(resp);
    if (!token) {
      pendingLinkOpenRef.current = false;
      clearPlaidStoredState();
      setMessage(getApiMessage(resp, 'Unable to reconnect. Please try again.'));
      return;
    }
    setLinkToken(token);
    writePlaidStoredValue(PLAID_LINK_TOKEN_STORAGE_KEY, token);
  }, [executeLinkToken, loadingLinkToken, loadingExchange]);

  const handleRefresh = React.useCallback(async () => {
    if (loadingHoldings) {
      setMessage('Refreshing holdings…');
      return;
    }
    setMessage('Refreshing holdings…');

    if (!loadingRefreshRequest && !refreshRequestInFlightRef.current) {
      refreshRequestInFlightRef.current = true;
      executeRefreshRequest()
        .catch(() => null)
        .finally(() => {
          refreshRequestInFlightRef.current = false;
        });
    }

    const resp = await executeHoldings();
    if (!resp || resp.success === false) {
      setMessage(getApiMessage(resp, 'Unable to refresh holdings.'));
      return;
    }
    setMessage('');
  }, [executeHoldings, executeRefreshRequest, loadingHoldings, loadingRefreshRequest]);

  const handleToggleAccount = React.useCallback(async ({ accountId, selected }) => {
    if (loadingAccountUpdate) return;
    setMessage('Updating accounts…');
    const resp = await executeAccountUpdate({ updates: [{ accountId, selected }] });
    if (!resp || resp.success === false) {
      setMessage(getApiMessage(resp, 'Unable to update accounts.'));
      return;
    }
    await executeHoldings();
    setMessage('');
  }, [executeAccountUpdate, executeHoldings, loadingAccountUpdate]);

  const handleDisconnect = React.useCallback(async (plaidItemRowId) => {
    if (loadingDisconnect) return;
    setMessage('Disconnecting…');
    const resp = await executeDisconnect({ plaidItemRowId });
    if (!resp || resp.success === false) {
      setMessage(getApiMessage(resp, 'Unable to disconnect.'));
      return;
    }
    await executeHoldings();
    setMessage('Disconnected.');
  }, [executeDisconnect, executeHoldings, loadingDisconnect]);

  const handleRequestSupport = React.useCallback(async (symbol) => {
    if (loadingSupport) return;
    setMessage('Sending support request…');
    const resp = await executeRequestSupport({ symbol });
    if (!resp || resp.success === false) {
      setMessage(getApiMessage(resp, 'Unable to send support request.'));
      return;
    }
    setMessage(`Support request sent for ${symbol}.`);
  }, [executeRequestSupport, loadingSupport]);

  const handleHideEntry = React.useCallback((hiddenEntryKey) => {
    const nextKey = String(hiddenEntryKey || '').trim();
    if (!nextKey) {
      return;
    }
    setHiddenEntryKeys((prev) => {
      if (prev.includes(nextKey)) {
        return prev;
      }
      return [...prev, nextKey];
    });
    setMessage('Entry hidden. Use "Unhide Hidden Entries" to restore.');
  }, []);

  const handleUnhideEntries = React.useCallback(() => {
    setHiddenEntryKeys([]);
    setMessage('All hidden entries are visible again.');
  }, []);

  const resetPanelSize = React.useCallback(() => {
    setPanelHeight(BROKER_TABLE_DEFAULT_HEIGHT);
    setPanelWidthPercent(BROKER_PANEL_DEFAULT_WIDTH_PERCENT);
  }, []);

  const stopTableResize = React.useCallback(() => {
    if (!tableResizeStateRef.current.active) {
      return;
    }
    tableResizeStateRef.current.active = false;
    clearBrokerResizeBodyClasses();
  }, []);

  const moveTableResize = React.useCallback((event) => {
    if (!tableResizeStateRef.current.active) {
      return;
    }
    const { mode } = tableResizeStateRef.current;
    if (mode === BROKER_RESIZE_MODE_Y || mode === BROKER_RESIZE_MODE_BOTH) {
      const deltaY = event.clientY - tableResizeStateRef.current.startY;
      const nextHeight = clampBrokerTableHeight(tableResizeStateRef.current.startHeight + deltaY);
      setPanelHeight(nextHeight);
    }
    if (mode === BROKER_RESIZE_MODE_X || mode === BROKER_RESIZE_MODE_BOTH) {
      const containerWidth = Number(tableResizeStateRef.current.startContainerWidth);
      if (Number.isFinite(containerWidth) && containerWidth > 0) {
        const deltaX = event.clientX - tableResizeStateRef.current.startX;
        const deltaPct = (deltaX / containerWidth) * 100;
        const nextWidth = clampBrokerPanelWidthPercent(tableResizeStateRef.current.startWidthPercent + deltaPct);
        setPanelWidthPercent(nextWidth);
      }
    }
    event.preventDefault();
  }, []);

  const startTableResize = React.useCallback((event, mode) => {
    if (event.button !== 0) {
      return;
    }
    const parentWidth = Number(holdingsCardRef.current?.parentElement?.clientWidth);
    const fallbackWidth = Number(holdingsCardRef.current?.clientWidth);
    const startContainerWidth = Number.isFinite(parentWidth) && parentWidth > 0
      ? parentWidth
      : (Number.isFinite(fallbackWidth) && fallbackWidth > 0 ? fallbackWidth : 1);
    tableResizeStateRef.current.active = true;
    tableResizeStateRef.current.mode = mode;
    tableResizeStateRef.current.startX = event.clientX;
    tableResizeStateRef.current.startY = event.clientY;
    tableResizeStateRef.current.startContainerWidth = startContainerWidth;
    tableResizeStateRef.current.startHeight = panelHeight;
    tableResizeStateRef.current.startWidthPercent = panelWidthPercent;
    setBrokerResizeBodyClass(mode);
    event.preventDefault();
  }, [panelHeight, panelWidthPercent]);

  const startTableResizeY = React.useCallback((event) => {
    startTableResize(event, BROKER_RESIZE_MODE_Y);
  }, [startTableResize]);

  const startTableResizeX = React.useCallback((event) => {
    startTableResize(event, BROKER_RESIZE_MODE_X);
  }, [startTableResize]);

  const startTableResizeBoth = React.useCallback((event) => {
    startTableResize(event, BROKER_RESIZE_MODE_BOTH);
  }, [startTableResize]);

  React.useEffect(() => {
    window.addEventListener('mousemove', moveTableResize);
    window.addEventListener('mouseup', stopTableResize);
    return () => {
      window.removeEventListener('mousemove', moveTableResize);
      window.removeEventListener('mouseup', stopTableResize);
      clearBrokerResizeBodyClasses();
    };
  }, [moveTableResize, stopTableResize]);

  const accountMetaById = React.useMemo(() => {
    const byId = new Map();
    (connections || []).forEach((connection) => {
      const institutionName = String(connection?.institutionName || '').trim() || 'Broker';
      (connection?.accounts || []).forEach((account) => {
        const accountId = String(account?.accountId || '').trim();
        if (!accountId) return;
        byId.set(accountId, {
          accountName: String(account?.name || '').trim() || 'Account',
          accountMask: account?.mask ? `••${String(account.mask)}` : '',
          brokerName: institutionName,
        });
      });
    });
    return byId;
  }, [connections]);

  const tableRows = React.useMemo(() => {
    return (holdings || []).map((holding, index) => {
      const dailyPrediction = toFiniteNumberOrNull(holding?.predictions?.DailyPricePrediction);
      const weeklyPrediction = toFiniteNumberOrNull(holding?.predictions?.WeeklyPricePrediction);
      const gainLossPrediction = toFiniteNumberOrNull(holding?.predictions?.DailyPredictedGainLossPct);
      const weeklyGainLossPrediction = toFiniteNumberOrNull(holding?.predictions?.WeeklyPredictedGainLossPct);
      const close = toFiniteNumberOrNull(holding?.closePrice);
      const quantity = toFiniteNumberOrNull(holding?.quantity);
      const institutionValue = toFiniteNumberOrNull(holding?.institutionValue);
      const ticker = String(holding?.ticker || '').toUpperCase();
      const accountId = String(holding?.accountId || '').trim() || null;
      const securityId = String(holding?.securityId || '').trim() || null;
      const rowId = `${holding?.accountId || 'acct'}:${holding?.securityId || ticker || 'sec'}:${index}`;
      const accountMeta = accountMetaById.get(String(holding?.accountId || '')) || {};
      const holdingName = String(holding?.name || '').trim();
      const dailyPredictionDelta = Number.isFinite(dailyPrediction) && Number.isFinite(close)
        ? dailyPrediction - close
        : null;
      const weeklyPredictionDelta = Number.isFinite(weeklyPrediction) && Number.isFinite(close)
        ? weeklyPrediction - close
        : null;

      return {
        ...holding,
        rowId,
        ticker,
        quantity,
        close,
        dailyPrediction,
        weeklyPrediction,
        gainLossPrediction,
        weeklyGainLossPrediction,
        dailyPredictionDelta,
        weeklyPredictionDelta,
        institutionValue,
        brokerName: accountMeta.brokerName || String(holding?.institutionName || '').trim() || 'Broker',
        accountName: accountMeta.accountName || 'Account',
        accountMask: accountMeta.accountMask || '',
        brokerAccount: [
          accountMeta.brokerName || String(holding?.institutionName || '').trim() || 'Broker',
          accountMeta.accountName || 'Account',
          accountMeta.accountMask || '',
        ].filter(Boolean).join(' · '),
        accountId,
        securityId,
        hiddenEntryKey: getBrokerHiddenEntryKey({
          accountId,
          securityId,
          ticker,
          name: holdingName,
        }),
        currencyCode: String(holding?.currency || '').trim() || 'USD',
        supportStatus: holding?.supported ? 'Supported' : 'Needs Support',
      };
    });
  }, [accountMetaById, holdings]);

  const hiddenEntryKeySet = React.useMemo(() => new Set(hiddenEntryKeys), [hiddenEntryKeys]);

  const visibleTableRows = React.useMemo(
    () => tableRows.filter((row) => !hiddenEntryKeySet.has(String(row?.hiddenEntryKey || '').trim())),
    [tableRows, hiddenEntryKeySet],
  );

  const hiddenEntriesCount = React.useMemo(
    () => Math.max(0, tableRows.length - visibleTableRows.length),
    [tableRows.length, visibleTableRows.length],
  );

  const columns = React.useMemo(() => ([
    {
      name: 'ticker',
      header: 'Ticker',
      type: 'string',
      hideable: false,
      minWidth: 80,
      defaultFlex: 1,
      textAlign: 'center',
      headerAlign: 'center',
      render: ({ value, data }) => (
        <span className={data?.supported ? 'dt-broker-ticker' : 'dt-broker-ticker dt-broker-ticker-unsupported'}>
          {value || '—'}
        </span>
      ),
    },
    {
      name: 'name',
      header: 'Name',
      type: 'string',
      hideable: false,
      minWidth: 140,
      defaultFlex: 2,
      headerAlign: 'center',
      render: ({ value }) => (
        <span className="dt-broker-name" title={value || ''}>
          {value || '—'}
        </span>
      ),
    },
    {
      name: 'quantity',
      header: 'Qty',
      type: 'number',
      hideable: false,
      minWidth: 66,
      defaultFlex: 1,
      textAlign: 'end',
      headerAlign: 'center',
      render: ({ value }) => {
        const num = Number(value);
        return <span style={{ fontVariantNumeric: 'tabular-nums' }}>{Number.isFinite(num) ? num.toFixed(2) : '—'}</span>;
      },
    },
    {
      name: 'close',
      header: 'Close',
      type: 'number',
      hideable: false,
      minWidth: 90,
      defaultFlex: 1,
      textAlign: 'end',
      headerAlign: 'center',
      render: ({ value, data }) => (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
          {formatMoney(value, data?.currency)}
        </span>
      ),
    },
    {
      name: 'dailyPrediction',
      header: 'Daily Pred',
      type: 'number',
      hideable: false,
      minWidth: 100,
      defaultFlex: 1.15,
      textAlign: 'end',
      headerAlign: 'center',
      render: ({ value, data }) => {
        return (
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            {formatMoney(value, data?.currency)}
          </span>
        );
      },
    },
    {
      name: 'gainLossPrediction',
      header: 'Gain/Loss %',
      type: 'number',
      hideable: false,
      minWidth: 120,
      defaultFlex: 1.15,
      textAlign: 'center',
      headerAlign: 'center',
      render: ({ value }) => (
        <span style={{
          fontVariantNumeric: 'tabular-nums',
          color: gainLossTextColor(value),
        }}>
          {formatGainLossPct(value)}
        </span>
      ),
    },
    {
      name: 'weeklyPrediction',
      header: 'Weekly Pred',
      type: 'number',
      hideable: false,
      minWidth: 100,
      defaultFlex: 1.15,
      textAlign: 'end',
      headerAlign: 'center',
      render: ({ value, data }) => {
        return (
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            {formatMoney(value, data?.currency)}
          </span>
        );
      },
    },
    {
      name: 'weeklyGainLossPrediction',
      header: 'Weekly G/L %',
      type: 'number',
      minWidth: 170,
      textAlign: 'center',
      headerAlign: 'center',
      visible: false,
      render: ({ value }) => (
        <span style={{
          fontVariantNumeric: 'tabular-nums',
          color: gainLossTextColor(value),
        }}>
          {formatGainLossPct(value)}
        </span>
      ),
    },
    {
      name: 'dailyPredictionDelta',
      header: 'Daily Δ',
      type: 'number',
      minWidth: 150,
      textAlign: 'end',
      headerAlign: 'center',
      visible: false,
      render: ({ value, data }) => (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
          {formatSignedMoneyDelta(value, data?.currency)}
        </span>
      ),
    },
    {
      name: 'weeklyPredictionDelta',
      header: 'Weekly Δ',
      type: 'number',
      minWidth: 150,
      textAlign: 'end',
      headerAlign: 'center',
      visible: false,
      render: ({ value, data }) => (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
          {formatSignedMoneyDelta(value, data?.currency)}
        </span>
      ),
    },
    {
      name: 'institutionValue',
      header: 'Mkt Value',
      type: 'number',
      hideable: false,
      minWidth: 95,
      defaultFlex: 1.05,
      textAlign: 'end',
      headerAlign: 'center',
      render: ({ value, data }) => (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
          {formatMoney(value, data?.currency)}
        </span>
      ),
    },
    {
      name: 'brokerAccount',
      header: 'Broker/Acct',
      type: 'string',
      hideable: false,
      minWidth: 180,
      defaultFlex: 1.35,
      headerAlign: 'center',
      render: ({ value }) => <span title={value || ''}>{value || '—'}</span>,
    },
    {
      name: 'brokerName',
      header: 'Broker',
      type: 'string',
      minWidth: 160,
      visible: false,
      headerAlign: 'center',
      render: ({ value }) => <span title={value || ''}>{value || '—'}</span>,
    },
    {
      name: 'accountName',
      header: 'Account',
      type: 'string',
      minWidth: 180,
      visible: false,
      headerAlign: 'center',
      render: ({ value }) => <span title={value || ''}>{value || '—'}</span>,
    },
    {
      name: 'accountMask',
      header: 'Mask',
      type: 'string',
      minWidth: 110,
      textAlign: 'center',
      headerAlign: 'center',
      visible: false,
      render: ({ value }) => <span>{value || '—'}</span>,
    },
    {
      name: 'currencyCode',
      header: 'Currency',
      type: 'string',
      minWidth: 110,
      textAlign: 'center',
      headerAlign: 'center',
      visible: false,
    },
    {
      name: 'supportStatus',
      header: 'Support',
      type: 'string',
      hideable: false,
      minWidth: 140,
      defaultFlex: 1.1,
      textAlign: 'center',
      headerAlign: 'center',
      render: ({ data }) => {
        if (data?.supported) {
          return <span className="dt-broker-supported">Supported</span>;
        }
        const symbol = data?.ticker;
        return (
          <button
            type="button"
            className="dt-broker-request"
            onClick={() => handleRequestSupport(symbol)}
            disabled={!symbol || loadingSupport}
            title="Request support for this ticker"
          >
            Request Support
          </button>
        );
      },
    },
  ].map((column) => ({
    ...column,
    sortable: column.sortable !== false,
  }))), [handleRequestSupport, loadingSupport]);

  const defaultSortInfo = React.useMemo(() => ([
    { name: 'ticker', dir: 1 },
  ]), []);

  const defaultFilterValue = React.useMemo(
    () => columns.map(({ name, type }) => ({
      name,
      type,
      operator: type === 'string' ? 'contains' : 'inrange',
      value: null,
    })),
    [columns],
  );

  const renderRowContextMenu = React.useCallback((menuProps, details) => {
    const rowData = details?.rowProps?.data;
    const hiddenEntryKey = String(rowData?.hiddenEntryKey || '').trim();
    if (!hiddenEntryKey) {
      return null;
    }
    const ticker = String(rowData?.ticker || '').trim();
    const rowName = String(rowData?.name || '').trim();
    const rowLabel = ticker || rowName || 'entry';

    menuProps.items = [
      {
        label: `Hide ${rowLabel}`,
        itemId: 'hideEntry',
        onClick: () => {
          handleHideEntry(hiddenEntryKey);
          if (details?.grid && typeof details.grid.hideRowContextMenu === 'function') {
            details.grid.hideRowContextMenu();
          }
        },
      },
    ];
    return undefined;
  }, [handleHideEntry]);

  if (!showBrokerPortfolio) {
    return (
      <section className="stockSec dt-stockSec-strategies dt-stockSec-broker">
        <div className="container-fluid">
          <DashboardMenu />
          <div className="dt-market-analysis-page">
            <div className="dt-page-head">
              <div className="dt-page-head-left">
                <div className="dt-breadcrumb">Dashboard / Broker Portfolio</div>
                <h1 className="dt-page-title">Broker Portfolio</h1>
                <p className="dt-page-subtitle">This feature is not enabled.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="stockSec dt-stockSec-strategies dt-stockSec-broker">
      <div className="container-fluid">
        <DashboardMenu />

        <div className="dt-broker-page">
          <div className="dt-page-head" style={{ width: `${panelWidthPercent}%` }}>
            <div className="dt-page-head-left">
              <div className="dt-breadcrumb">Dashboard / Broker Portfolio</div>
              <h1 className="dt-page-title">Broker Portfolio</h1>
              <p className="dt-page-subtitle">
                Connect your broker accounts (read-only) and view DeepTrading predictions for your holdings.
              </p>
            </div>
            <div className="dt-page-head-actions">
              <button
                type="button"
                className="dt-btn dt-btn-accent dt-broker-btn dt-action-btn"
                onClick={handleConnect}
                title={loadingLinkToken || loadingExchange ? 'Preparing secure broker link…' : 'Connect a broker account (read-only)'}
              >
                Connect Broker
              </button>
              <button
                type="button"
                className="dt-btn dt-btn-primary dt-broker-btn dt-action-btn"
                onClick={handleRefresh}
                disabled={loadingHoldings}
                title={loadingHoldings ? 'Refreshing holdings…' : 'Refresh broker holdings'}
              >
                Refresh
              </button>
            </div>
          </div>

          {message && <div className="dt-broker-message">{message}</div>}

          <div className="dt-grid">
            <div
              className="dt-card dt-card-pad dt-broker-connections-card"
              style={{ width: `${panelWidthPercent}%` }}
            >
              <div className="dt-card-header dt-card-header-tight">
                <div>
                  <h2 className="dt-card-title">Connected Accounts</h2>
                  <div className="dt-card-note">Choose which brokerage accounts to include.</div>
                </div>
              </div>

              {!connections.length ? (
                <div className="dt-broker-empty">No broker connections yet. Click “Connect Broker” to get started.</div>
              ) : (
                <div className="dt-broker-connections">
                  {connections.map((item) => (
                    <div className="dt-broker-connection" key={item.id}>
                      <div className="dt-broker-connection-head">
                        <div className="dt-broker-connection-title">{item.institutionName || 'Broker'}</div>
                        <div className="dt-broker-connection-actions">
                          <button
                            type="button"
                            className="dt-broker-reconnect"
                            onClick={() => handleReconnect(item.id)}
                            disabled={loadingLinkToken || loadingExchange}
                            title="Reconnect if Plaid asks you to re-authenticate"
                          >
                            Reconnect
                          </button>
                          <button
                            type="button"
                            className="dt-broker-disconnect"
                            onClick={() => handleDisconnect(item.id)}
                            disabled={loadingDisconnect}
                          >
                            Disconnect
                          </button>
                        </div>
                      </div>
                      <div className="dt-broker-accounts">
                        {(item.accounts || []).map((acct) => (
                          <label key={acct.accountId} className="dt-broker-account">
                            <input
                              type="checkbox"
                              checked={acct.selected === true}
                              onChange={(e) => handleToggleAccount({ accountId: acct.accountId, selected: e.target.checked })}
                              disabled={loadingAccountUpdate}
                            />
                            <span className="dt-broker-account-name">{acct.name}</span>
                            {acct.mask && <span className="dt-broker-account-mask">••{acct.mask}</span>}
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div
              className="dt-card dt-card-results dt-broker-holdings-card"
              ref={holdingsCardRef}
              style={{ height: `${panelHeight}px`, width: `${panelWidthPercent}%` }}
            >
              <div className="dt-card-pad dt-card-header dt-card-header-tight">
                <div>
                  <h2 className="dt-card-title">Holdings</h2>
                  <div className="dt-card-note">
                    Untracked tickers appear as “Request Support”.
                    {unsupported.length ? ` (${unsupported.length} untracked)` : ''}
                    {loadingHoldings ? ' Loading…' : ''}
                  </div>
                  <div className="dt-broker-tip-row">
                    <div className="dt-broker-tip">
                      Tip: drag panel edges to resize, or right-click a row and choose Hide Entry.
                    </div>
                    <button
                      type="button"
                      className="dt-panel-reset-btn dt-broker-unhide-btn"
                      onClick={handleUnhideEntries}
                      disabled={hiddenEntriesCount === 0}
                      title="Restore all hidden holdings rows"
                    >
                      Unhide Hidden Entries{hiddenEntriesCount > 0 ? ` (${hiddenEntriesCount})` : ''}
                    </button>
                    <button
                      type="button"
                      className="dt-panel-reset-btn"
                      onClick={resetPanelSize}
                      title="Reset panel width and height to default"
                    >
                      Reset Panel Size
                    </button>
                  </div>
                </div>
              </div>
              <div className="dt-broker-table">
                <div className="dt-broker-table-grid">
                  <Grid
                    preference={BROKER_GRID_PREFERENCE}
                    idProperty="rowId"
                    dataSource={visibleTableRows}
                    columns={columns}
                    className="dt-broker-grid"
                    sortable
                    defaultSortInfo={defaultSortInfo}
                    enableFiltering
                    defaultFilterValue={defaultFilterValue}
                    enableColumnFilterContextMenu={false}
                    showColumnMenuTool
                    showColumnMenuSortOptions={false}
                    showColumnMenuGroupOptions={false}
                    showColumnMenuFilterOptions={false}
                    showColumnMenuLockOptions={false}
                    renderRowContextMenu={renderRowContextMenu}
                    rowContextMenuPosition="fixed"
                    menuPortalContainer={typeof document !== 'undefined' ? document.body : null}
                    loading={loadingHoldings}
                    rowHeight={38}
                    headerHeight={48}
                    filterRowHeight={32}
                    style={{ height: '100%' }}
                  />
                </div>
                <div
                  className="dt-broker-table-resizer-y"
                  onMouseDown={startTableResizeY}
                  title="Drag vertically to resize panel"
                />
                <div
                  className="dt-broker-table-resizer-x"
                  onMouseDown={startTableResizeX}
                  title="Drag horizontally to resize panel"
                />
                <div
                  className="dt-broker-table-corner-resizer"
                  onMouseDown={startTableResizeBoth}
                  title="Drag to resize panel"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BrokerPortfolio;
